<template>
	<button
		type="button"
		:disabled="disabled"
		:class="['help-button', `help-button--${variant}`, { 'help-button--icon': iconOnly }]"
		v-bind="$attrs"
		v-on="$listeners"
	>
		<slot />
	</button>
</template>

<script>
/**
 * The help assistant's button (AB#27211), the Vue counterpart of syrenis-ui's `Button` as this
 * feature uses it.
 *
 * Exists because Vuetify's `v-btn` cannot be made to match: it uppercases its label, applies its own
 * letter spacing, and enforces a minimum width (64px default, 50px small), so every control built on
 * it came out wider and shoutier than the React original. A plain button with the design tokens is
 * both simpler and actually identical.
 *
 * Sizing follows React: 32px tall, 13px label, token radius, and no hover lift (the shared React
 * Button applies one to every variant, which its own call sites override).
 */
export default {
	name: 'help-button',
	inheritAttrs: false,
	props: {
		/** `primary` is filled, `outline` is bordered, `ghost` is bare until hovered. */
		variant: {
			type: String,
			default: 'outline',
			validator: value => ['primary', 'outline', 'ghost'].includes(value)
		},
		/** Square, for a control whose content is a single glyph. */
		iconOnly: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		}
	}
}
</script>

<style lang="scss" scoped>
.help-button {
	display: inline-flex;
	flex: none;
	gap: 6px;
	align-items: center;
	justify-content: center;
	min-height: 32px;
	padding: 0 12px;
	font-family: inherit;
	font-size: 0.8125rem;
	font-weight: 500;
	line-height: 1.2;
	text-transform: none;
	letter-spacing: normal;
	cursor: pointer;
	border: 1px solid transparent;
	border-radius: var(--radius, 8px);
	transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

	&:disabled {
		cursor: not-allowed;
		opacity: 0.5;
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}

	&:focus-visible {
		outline: 2px solid var(--primary, #406A94);
		outline-offset: 2px;
	}

	&--primary {
		color: var(--primary-foreground, #FFFFFF);
		background-color: var(--primary, #406A94);

		&:hover:not(:disabled) {
			opacity: 0.9;
		}
	}

	&--outline {
		color: var(--foreground, #4A5568);
		background-color: var(--card, #FFFFFF);
		border-color: var(--input, #E0E2E5);

		&:hover:not(:disabled) {
			background-color: var(--muted, #F5F5F5);
		}
	}

	&--ghost {
		color: var(--muted-foreground, #666E7A);
		background: none;

		&:hover:not(:disabled) {
			color: var(--foreground, #4A5568);
			background-color: var(--muted, #F5F5F5);
		}
	}

	&--icon {
		min-width: 32px;
		padding: 0;
	}
}
</style>
